import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Loader2, Plus, RefreshCw, ShieldCheck, Users as UsersIcon, Link2, Radio } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ErrorBox, Field, NoticeBox, dangerBtn, ghostBtn, inputCls, primaryBtn } from "@/components/auth/AuthUi";
import OptionsAccess from "@/components/admin/OptionsAccess";
import AccessOverview from "@/components/admin/AccessOverview";

const STATUS_STYLE = {
  ACTIVE: "bg-[#00C805]/15 text-[#00C805]",
  PENDING_SETUP: "bg-[#FFCC00]/15 text-[#FFCC00]",
  DEACTIVATED: "bg-[#475569]/30 text-slate-400",
};
const STATUS_LABEL = { ACTIVE: "Active", PENDING_SETUP: "Pending setup", DEACTIVATED: "Deactivated" };

function StatusBadge({ user }) {
  return (
    <span className="inline-flex gap-1">
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${STATUS_STYLE[user.status] || ""}`}>{STATUS_LABEL[user.status] || user.status}</span>
      {user.locked && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#FF3B30]/15 text-[#FF6b62]">Locked</span>}
    </span>
  );
}

function BrokerCell({ b }) {
  if (!b?.linked) return <span className="text-slate-600">Not linked</span>;
  const tone = b.session_status === "CONNECTED" ? "text-[#00C805]" : b.session_status === "EXPIRED" || b.session_status === "ERROR" ? "text-[#FF6b62]" : "text-slate-400";
  return <span className="mono"><span className="text-slate-200">{b.broker_user_id}</span> <span className={tone}>{b.session_status}</span></span>;
}

// Shows a one-time link exactly once. The raw token only ever lives in this component's memory.
function OneTimeLink({ link, onDone }) {
  const url = `${window.location.origin}${link.setup_path}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link and copy it manually"); }
  };
  return (
    <div className="space-y-3" data-testid="one-time-link">
      <NoticeBox>This {link.purpose === "RESET" ? "reset" : "setup"} link is shown <b>once</b>. Send it to the user through a private channel. It works a single time and expires {dateTime(link.expires_at)}.</NoticeBox>
      <div className="flex gap-2">
        <input readOnly value={url} className={`${inputCls} text-[11px]`} data-testid="one-time-link-url" onFocus={(e) => e.target.select()} />
        <button onClick={copy} className={ghostBtn} data-testid="one-time-link-copy"><Copy className="h-4 w-4" /></button>
      </div>
      <button onClick={onDone} className={`w-full ${primaryBtn}`}>Done — I have saved the link</button>
    </div>
  );
}

function RolePicker({ roles, value, onChange, disabled }) {
  const change = (role, checked) => {
    let next = checked ? [...new Set([...value, role])] : value.filter((x) => x !== role);
    if (role === "LIVE_TRADER" && checked && !next.includes("TRADER")) next.push("TRADER");
    if (role === "TRADER" && !checked) next = next.filter((x) => x !== "LIVE_TRADER");
    onChange(next);
  };
  return (
    <div className="flex gap-3">
      {roles.map((r) => (
        <label key={r} className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" disabled={disabled} checked={value.includes(r)} data-testid={`role-${r}`}
            onChange={(e) => change(r, e.target.checked)} />
          {r}
        </label>
      ))}
    </div>
  );
}

function Readiness({ ok, children }) {
  return <span className={`text-[10px] font-semibold px-2 py-1 rounded-sm border ${ok
    ? "bg-[#00C805]/10 text-[#008a04] border-[#00C805]/30"
    : "bg-[#FFCC00]/10 text-[#9a7200] border-[#FFCC00]/40"}`}>{ok ? "✓" : "—"} {children}</span>;
}

function BrokerAccess({ user, accounts, deployment, busy, onChange, nameOf = (x) => x }) {
  const [change, setChange] = useState(null);
  const [reason, setReason] = useState("");
  const hasTrader = (user.roles || []).includes("TRADER") || (user.roles || []).includes("LIVE_TRADER");
  const hasLive = (user.roles || []).includes("LIVE_TRADER");

  const submit = async () => {
    const path = change.mode === "SHADOW" ? "shadow-live" : "live";
    await onChange(`broker-${change.mode.toLowerCase()}`,
      () => api.post(`/admin/broker-accounts/${change.account.id}/${path}`,
        { enabled: change.enabled, reason: reason.trim() }).then(() => {
          toast.success(`${change.mode} ${change.enabled ? "enabled" : "disabled"}`);
          setChange(null); setReason("");
        }));
  };

  return (
    <section className="space-y-3 border-t border-[var(--term-border)] pt-4" data-testid="broker-access">
      <div>
        <h3 className="text-sm font-bold">Futures authorisation (SHADOW / LIVE keys)</h3>
        <p className="mt-1 text-[10px] text-slate-500">Futures SHADOW / LIVE authorisation for the linked Zerodha account (Options keys are under Options Access below). Authorization readiness only. Risk gates, reconciliation, allowlists, quantity verification, halts, capital checks and broker connection still apply.</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Readiness ok={user.status === "ACTIVE"}>User {user.status}</Readiness>
        <Readiness ok={hasTrader}>TRADER {hasTrader ? "present" : "missing"}</Readiness>
        <Readiness ok={hasLive}>LIVE_TRADER {hasLive ? "present" : "missing"}</Readiness>
        <Readiness ok={deployment.live}>LIVE deployment {deployment.live ? "enabled" : "disabled"}</Readiness>
        <Readiness ok={deployment.shadow}>SHADOW deployment {deployment.shadow ? "enabled" : "disabled"}</Readiness>
      </div>
      {!accounts.length && <div className="term-panel-2 rounded-sm p-3 text-xs text-slate-500">No broker account has been linked. The user must connect Zerodha from Broker Session first.</div>}
      {accounts.map((account) => {
        const active = account.link_status === "ACTIVE";
        const connected = account.session_status === "CONNECTED";
        const liveBlocked = !!account.shadow_enabled;
        const shadowBlocked = !!account.live_enabled;
        const noFutures = account.trading_products === "OPTIONS_ONLY";
        return (
          <div key={account.id} className="term-panel-2 rounded-sm p-3 space-y-3" data-testid={`broker-account-${account.id}`}>
            <div className="flex items-start gap-2 flex-wrap">
              <Link2 className="h-4 w-4 text-[#2563EB] mt-0.5" />
              <div className="mr-auto">
                <div className="text-xs font-semibold">{(account.broker || "broker").toUpperCase()} · <span className="mono">{account.broker_user_id}</span></div>
                <div className="text-[10px] text-slate-500">{account.link_status} · last connected {dateTime(account.last_connected_at)}</div>
              </div>
              <Readiness ok={active}>Account {active ? "enabled" : "unlinked"}</Readiness>
              <Readiness ok={connected}>Broker {connected ? "connected" : "not connected"}</Readiness>
            </div>
            {(account.history || []).length > 0 && (
              <div className="text-[10px] text-slate-500" data-testid={`broker-account-history-${account.id}`}
                title={(account.history || []).map((h) => `${h.id} linked ${dateTime(h.linked_at)} · ${h.superseded_reason || h.unlink_reason || ""}`).join(" | ")}>
                {account.history.length} earlier duplicate record{account.history.length === 1 ? "" : "s"} of this account kept as history
              </div>
            )}
            {noFutures && !account.shadow_enabled && !account.live_enabled ? (
              <div className="text-[11px] text-slate-500" data-testid={`futures-not-in-products-${account.id}`}>
                This account trades Options only — Futures SHADOW / LIVE do not apply. Change its trading products under Options Access to add Futures.
              </div>
            ) : <div className="grid gap-2 sm:grid-cols-2">
              {[{
                mode: "SHADOW", enabled: !!account.shadow_enabled, blocked: shadowBlocked,
                deployment: deployment.shadow, by: account.shadow_enabled_by,
                at: account.shadow_enabled_at || account.shadow_disabled_at,
                reason: account.shadow_enabled_reason,
              }, {
                mode: "LIVE", enabled: !!account.live_enabled, blocked: liveBlocked,
                deployment: deployment.live, by: account.live_enabled_by,
                at: account.live_enabled_at || account.live_disabled_at,
                reason: account.live_enabled_reason,
              }].map((item) => {
                const unavailable = !active || !hasLive || (item.blocked && !item.enabled);
                const why = !active ? "Account is not actively linked" : !hasLive ? "LIVE_TRADER permission is missing"
                  : item.blocked ? `Disable ${item.mode === "LIVE" ? "SHADOW" : "LIVE"} first` : !item.deployment ? "Deployment flag is disabled" : "Eligible for authorization";
                return <div key={item.mode} className="rounded-sm border border-[var(--term-border)] p-2 space-y-2">
                  <div className="flex items-center gap-2"><Radio className="h-3.5 w-3.5" />
                    <span className="text-xs font-bold">{item.mode === "SHADOW" ? "Futures SHADOW" : "Futures LIVE"}</span>
                    <span className={`ml-auto text-[10px] font-bold ${item.enabled ? "text-[#00C805]" : "text-slate-500"}`}>{item.enabled ? "ENABLED" : "DISABLED"}</span>
                  </div>
                  <div className="text-[10px] text-slate-500">{why}</div>
                  {(item.at || item.reason) && <div className="text-[10px] text-slate-500">Updated {dateTime(item.at)}{item.by ? ` by ${nameOf(item.by)}` : ""}{item.reason ? ` · ${item.reason}` : ""}</div>}
                  <button type="button" disabled={!!busy || (unavailable && !item.enabled)}
                    className={item.enabled ? dangerBtn : primaryBtn}
                    data-testid={`${item.mode.toLowerCase()}-toggle-${account.id}`}
                    onClick={() => { setReason(""); setChange({ account, mode: item.mode, enabled: !item.enabled }); }}>
                    {item.enabled ? `Disable ${item.mode}` : `Enable ${item.mode}`}
                  </button>
                </div>;
              })}
            </div>}
          </div>
        );
      })}
      <Dialog open={!!change} onOpenChange={(open) => { if (!open && !busy) { setChange(null); setReason(""); } }}>
        <DialogContent className="term-panel text-[var(--term-primary)]" data-testid="broker-access-dialog">
          <DialogHeader><DialogTitle>{change?.enabled ? "Enable" : "Disable"} Futures {change?.mode}</DialogTitle>
            <DialogDescription>This changes the existing account authorization flag only. It does not bypass any execution safeguard.</DialogDescription></DialogHeader>
          <Field label="Reason (recorded in audit history)"><input autoFocus value={reason} maxLength={500}
            onChange={(e) => setReason(e.target.value)} className={inputCls} data-testid="broker-access-reason" /></Field>
          <div className="flex justify-end gap-2"><button className={ghostBtn} onClick={() => setChange(null)}>Cancel</button>
            <button disabled={!!busy || reason.trim().length < 3} className={change?.enabled ? primaryBtn : dangerBtn}
              onClick={submit} data-testid="broker-access-confirm">Confirm</button></div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function CreateUserDialog({ open, onOpenChange, roles, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState(["TRADER"]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const reset = () => { setEmail(""); setName(""); setChosen(["TRADER"]); setErr(""); setLink(null); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/users", { email, name, roles: chosen });
      setLink(data.setup_link);
      onCreated();
    } catch (e2) { setErr(apiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!link) close(v); }}>
      <DialogContent className="bg-[#12151C] border-[#2A2F3A] text-slate-100" data-testid="create-user-dialog"
        onInteractOutside={(e) => { if (link) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>{link ? "User created" : "Create user"}</DialogTitle>
          <DialogDescription className="text-slate-500">{link ? `${email} can now set their password.` : "The user sets their own password from a one-time link."}</DialogDescription>
        </DialogHeader>
        {link ? <OneTimeLink link={link} onDone={() => close(false)} /> : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="create-email" /></Field>
            <Field label="Name"><input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="create-name" maxLength={120} /></Field>
            <Field label="Roles"><RolePicker roles={roles} value={chosen} onChange={setChosen} /></Field>
            <ErrorBox testId="create-error">{err}</ErrorBox>
            <button type="submit" disabled={busy || !chosen.length} className={`w-full ${primaryBtn}`} data-testid="create-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create user
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DRAWER_TABS = [["profile", "Profile"], ["access", "Access"], ["security", "Security"], ["history", "History"]];
const HISTORY_FILTERS = [["ALL", "All"], ["SIGNIN", "Sign-ins"], ["ACCOUNT", "Account changes"], ["BROKER", "Broker"]];
const historyKind = (action) => (/LOGIN|LOGOUT|SESSION|PASSWORD/.test(action || "") ? "SIGNIN"
  : /BROKER/.test(action || "") ? "BROKER" : "ACCOUNT");

function UserDrawer({ userId, roles, me, users = [], onClose, onChanged }) {
  // UI/UX audit Phase 5: one long scroll mixed profile, LIVE switches, password reset and Deactivate. Tabs now, with
  // every irreversible action in the Security tab's danger zone, and people named instead of shown as ids.
  const [tab, setTab] = useState("profile");
  const [hist, setHist] = useState("ALL");
  const [typed, setTyped] = useState("");
  const nameOf = (id) => { const x = users.find((p) => p.id === id); return x ? (x.name || x.email) : id ? `${String(id).slice(0, 8)}…` : null; };
  const [u, setU] = useState(null);
  const [events, setEvents] = useState([]);
  const [edit, setEdit] = useState({ name: "", email: "", roles: [] });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState(null);
  const [revokeOnReset, setRevokeOnReset] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [brokerAccounts, setBrokerAccounts] = useState([]);
  const [deployment, setDeployment] = useState({ live: false, shadow: false });
  const [accessData, setAccessData] = useState(null);       // OptionsAccess's /admin/options-access answer, for the overview
  const onAccessData = useCallback((data, analysis) => setAccessData({ data, analysis }), []);

  const load = useCallback(async () => {
    const [{ data }, { data: brokerData }] = await Promise.all([
      api.get(`/admin/users/${userId}`), api.get("/admin/broker-accounts")]);
    setU(data);
    setEdit({ name: data.name || "", email: data.email, roles: data.roles || [] });
    setBrokerAccounts((brokerData.accounts || []).filter((a) => a.owner_user_id === userId));
    setDeployment({ live: !!brokerData.live_trading_enabled, shadow: !!brokerData.shadow_trading_enabled });
    api.get(`/admin/users/${userId}/audit`, { params: { limit: 50 } }).then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, [userId]);

  useEffect(() => { setLink(null); setPreview(null); setReason(""); setAck(false); setTyped(""); setErr(""); setTab("profile"); load().catch((e) => setErr(apiError(e))); }, [load]);

  const act = async (label, fn) => {
    setErr(""); setBusy(label);
    try { await fn(); await load(); onChanged(); }
    catch (e) { setErr(apiError(e)); }
    finally { setBusy(""); }
  };

  const isSelf = u && me && u.id === me.id;
  const dirty = u && (edit.name !== (u.name || "") || edit.email !== u.email || [...edit.roles].sort().join() !== [...(u.roles || [])].sort().join());

  return (
    <Sheet open={!!userId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className={`bg-[#12151C] border-[#2A2F3A] text-slate-100 w-full ${tab === "access" ? "sm:max-w-5xl" : "sm:max-w-xl"} overflow-y-auto`} data-testid="user-drawer">
        <SheetHeader>
          <SheetTitle className="text-slate-100">{u?.name || "User"}</SheetTitle>
          <SheetDescription className="text-slate-500">{u?.email}</SheetDescription>
        </SheetHeader>
        {!u ? <div className="p-4"><ErrorBox>{err}</ErrorBox>{!err && <Loader2 className="h-5 w-5 animate-spin" />}</div> : (
          <div className="space-y-5 py-4">
            <ErrorBox testId="drawer-error">{err}</ErrorBox>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-500">Status </span><StatusBadge user={u} /></div>
              <div><span className="text-slate-500">Zerodha </span><BrokerCell b={u.broker_account} /></div>
              <div><span className="text-slate-500">Created </span>{dateTime(u.created_at)}</div>
              <div><span className="text-slate-500">Created by </span>{u.created_by?.name || u.created_by?.email || (u.created_via === "MIGRATION" ? "System migration" : "—")}</div>
              <div><span className="text-slate-500">Last sign-in </span>{dateTime(u.last_login_at)}</div>
              <div><span className="text-slate-500">Active sessions </span>{(u.active_sessions || []).length}</div>
              {u.status === "DEACTIVATED" && <div className="col-span-2"><span className="text-slate-500">Deactivated </span>{dateTime(u.deactivated_at)} — {u.deactivation_reason}</div>}
            </div>

            <div className="flex border-b border-[#2A2F3A]" role="tablist" data-testid="user-drawer-tabs">
              {DRAWER_TABS.map(([k, l]) => (
                <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`user-tab-${k}`}
                  className={`px-3 py-1.5 text-xs font-bold border-b-2 ${tab === k ? "border-[#2563EB] text-slate-100" : "border-transparent text-slate-500 hover:text-slate-200"}`}>{l}</button>))}
            </div>

            {tab === "profile" && <section className="space-y-3">
              <h3 className="text-xs font-bold text-slate-400">DETAILS</h3>
              <Field label="Name"><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} data-testid="edit-name" /></Field>
              <Field label="Email"><input type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className={inputCls} data-testid="edit-email" /></Field>
              <Field label="Roles" hint={isSelf ? "You cannot remove your own ADMIN role." : null}>
                <RolePicker roles={roles} value={edit.roles} onChange={(r) => setEdit({ ...edit, roles: r })} />
              </Field>
              <button disabled={!dirty || !!busy || !edit.roles.length} className={primaryBtn} data-testid="edit-save"
                onClick={() => act("save", () => api.patch(`/admin/users/${u.id}`, edit).then(() => toast.success("User updated")))}>
                {busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
              </button>
            </section>}

            {tab === "access" && <>
              <AccessOverview user={u} futuresAccounts={brokerAccounts} optionsData={accessData?.data} analysis={accessData?.analysis} />
              <div className="pt-2">
                <h2 className="text-base font-bold">Edit authorisations and limits</h2>
                <p className="text-xs text-slate-500">Every change asks for a reason and is recorded in the audit history. The overview above updates after each change.</p>
              </div>
              <BrokerAccess user={u} accounts={brokerAccounts} deployment={deployment} busy={busy} onChange={act} nameOf={nameOf} />
              <OptionsAccess userId={u.id} user={u} busy={busy} onChange={act} onData={onAccessData} />
            </>}

            {tab === "security" && u.status !== "DEACTIVATED" && (
              <section className="space-y-3">
                <h3 className="text-xs font-bold text-slate-400">{u.status === "PENDING_SETUP" ? "SETUP LINK" : "PASSWORD RESET"}</h3>
                {link ? <OneTimeLink link={link} onDone={() => setLink(null)} /> : (
                  <>
                    {u.status === "ACTIVE" && (
                      <label className="flex items-center gap-2 text-xs text-slate-300">
                        <input type="checkbox" checked={revokeOnReset} onChange={(e) => setRevokeOnReset(e.target.checked)} data-testid="reset-revoke" />
                        Also sign the user out of all sessions now
                      </label>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <button disabled={!!busy} className={ghostBtn} data-testid="reset-link-btn"
                        onClick={() => act("reset", async () => {
                          const { data } = await api.post(`/admin/users/${u.id}/reset-link`, { revoke_sessions: revokeOnReset });
                          setLink(data.link);
                        })}>
                        {busy === "reset" && <Loader2 className="h-4 w-4 animate-spin" />}
                        {u.status === "PENDING_SETUP" ? "Generate new setup link" : "Generate reset link"}
                      </button>
                      <button disabled={!!busy || !(u.active_sessions || []).length} className={ghostBtn} data-testid="revoke-sessions-btn"
                        onClick={() => act("revoke", () => api.post(`/admin/users/${u.id}/revoke-sessions`).then((r) => toast.success(`${r.data.sessions_revoked} session(s) signed out`)))}>
                        Sign out all sessions
                      </button>
                    </div>
                  </>
                )}
              </section>
            )}

            {tab === "security" && <section className="space-y-3 rounded-sm border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3" data-testid="danger-zone">
              <h3 className="text-xs font-bold text-[#FF6b62]">DANGER ZONE</h3>
              {u.status === "DEACTIVATED" ? (
                <button disabled={!!busy} className={primaryBtn} data-testid="reactivate-btn"
                  onClick={() => act("reactivate", () => api.post(`/admin/users/${u.id}/reactivate`).then(() => toast.success("User reactivated")))}>
                  {busy === "reactivate" && <Loader2 className="h-4 w-4 animate-spin" />} Reactivate user
                </button>
              ) : isSelf ? (
                <div className="text-xs text-slate-500">You cannot deactivate your own account.</div>
              ) : !preview ? (
                <button disabled={!!busy} className={dangerBtn} data-testid="deactivate-start"
                  onClick={() => act("preview", async () => { const { data } = await api.get(`/admin/users/${u.id}/deactivation-preview`); setPreview(data); })}>
                  Deactivate user…
                </button>
              ) : (
                <div className="space-y-3" data-testid="deactivate-panel">
                  <div className="text-xs text-slate-300">Deactivation immediately blocks sign-in and every existing session, disconnects the user's Zerodha session and stops their running strategies. <b>Open positions are not squared off.</b> All data is kept.</div>
                  {preview.running_strategies.length > 0 && (
                    <div className="text-xs"><div className="text-slate-500 mb-1">Running strategies that will be stopped:</div>
                      <ul className="list-disc pl-4">{preview.running_strategies.map((s) => <li key={s.trade_id}>{s.name} · {s.underlying || "—"} · {s.trading_environment}</li>)}</ul></div>
                  )}
                  {preview.open_positions.length > 0 && (
                    <div className="text-xs"><div className="text-[#FFCC00] mb-1">Open positions that will stay open:</div>
                      <ul className="list-disc pl-4">{preview.open_positions.map((p, i) => <li key={i}>{p.trade_name} · L{p.level_number} · SELL {p.sell_contract} / BUY {p.buy_contract} · {p.trading_environment}</li>)}</ul></div>
                  )}
                  <Field label="Reason (recorded in the audit log)">
                    <input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} data-testid="deactivate-reason" maxLength={500} />
                  </Field>
                  {preview.requires_acknowledgement && (
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="deactivate-ack" />
                      I understand the strategies above will stop and the open positions will remain open.
                    </label>
                  )}
                  <Field label={`Type the user's email (${u.email}) to confirm`}>
                    <input value={typed} onChange={(e) => setTyped(e.target.value)} className={inputCls} data-testid="deactivate-typed" autoComplete="off" />
                  </Field>
                  <div className="flex gap-2">
                    <button disabled={!!busy || reason.trim().length < 3 || (preview.requires_acknowledgement && !ack) || typed.trim().toLowerCase() !== String(u.email).toLowerCase()}
                      className={dangerBtn} data-testid="deactivate-confirm"
                      onClick={() => act("deactivate", async () => {
                        const { data } = await api.post(`/admin/users/${u.id}/deactivate`, { reason: reason.trim(), acknowledge_open_positions: ack });
                        setPreview(null); setReason(""); setAck(false); setTyped("");
                        if (data.failed_steps?.length) toast.warning(`Deactivated, but some steps need a retry: ${data.failed_steps.map((f) => f.step).join(", ")}`);
                        else toast.success("User deactivated");
                      })}>
                      {busy === "deactivate" && <Loader2 className="h-4 w-4 animate-spin" />} Confirm deactivation
                    </button>
                    <button className={ghostBtn} onClick={() => { setPreview(null); setTyped(""); }}>Cancel</button>
                  </div>
                </div>
              )}
            </section>}

            {tab === "history" && <section className="space-y-2">
              <h3 className="text-xs font-bold text-slate-400">ACCOUNT HISTORY</h3>
              <div className="text-[10px] text-slate-600">Sign-ins, account changes and broker links. Trading activity is in the Trading Book.</div>
              <div className="flex gap-1" data-testid="user-audit-filter">
                {HISTORY_FILTERS.map(([k, l]) => (
                  <button key={k} onClick={() => setHist(k)} className={`px-2 py-0.5 text-[10px] font-bold rounded-sm border ${hist === k ? "bg-[#2563EB] border-transparent text-white" : "border-[#2A2F3A] text-slate-400"}`}>{l}</button>))}
              </div>
              <div className="max-h-96 overflow-auto divide-y divide-[#20242c] border border-[#2A2F3A] rounded-sm" data-testid="user-audit">
                {events.filter((ev) => hist === "ALL" || historyKind(ev.action) === hist).length === 0 && <div className="p-2 text-xs text-slate-500">No events.</div>}
                {events.filter((ev) => hist === "ALL" || historyKind(ev.action) === hist).map((ev) => (
                  <div key={ev.id} className="px-2 py-1.5 text-[11px] flex gap-2">
                    <span className="text-slate-500 mono shrink-0">{dateTime(ev.ts)}</span>
                    <span className={ev.outcome === "SUCCESS" ? "text-slate-200" : "text-[#FF6b62]"}>{ev.action}</span>
                    {ev.actor_user_id && ev.actor_user_id !== u.id && <span className="text-slate-500" data-testid="audit-actor">by {nameOf(ev.actor_user_id)}</span>}
                  </div>
                ))}
              </div>
            </section>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [data, setData] = useState({ users: [], assignable_roles: ["ADMIN", "TRADER"] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/users", { params: status ? { status } : {} });
      setData(d); setErr("");
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? data.users.filter((u) => (u.email || "").includes(n) || (u.name || "").toLowerCase().includes(n)) : data.users;
  }, [data.users, q]);

  return (
    <div className="p-6 space-y-4" data-testid="admin-users-page">
      <div className="flex items-center gap-3 flex-wrap">
        <UsersIcon className="h-6 w-6 text-[#2563EB]" />
        <div className="mr-auto">
          <h1 className="text-lg font-bold tracking-tight">Users</h1>
          <div className="text-xs text-slate-500">Team accounts. Users are never deleted — deactivate to remove access.</div>
        </div>
        <input placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} w-56`} data-testid="users-search" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-40`} data-testid="users-status-filter">
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING_SETUP">Pending setup</option>
          <option value="DEACTIVATED">Deactivated</option>
        </select>
        <button onClick={load} className={ghostBtn} title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        <button onClick={() => setCreateOpen(true)} className={primaryBtn} data-testid="create-user-btn"><Plus className="h-4 w-4" /> Create user</button>
      </div>
      <ErrorBox>{err}</ErrorBox>

      <div className="term-panel rounded-sm overflow-x-auto">
        <table className="w-full text-xs" data-testid="users-table">
          <thead className="text-slate-500 border-b border-[#2A2F3A]">
            <tr>{["Name", "Email", "Roles", "Status", "Zerodha", "Products", "Last sign-in", "Created", "Created by"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} onClick={() => setOpenId(u.id)} className="gz-row cursor-pointer" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-2 text-slate-100 whitespace-nowrap">{u.name}{me?.id === u.id && <span className="ml-1 text-[10px] text-slate-500">(you)</span>}</td>
                <td className="px-3 py-2 mono">{u.email}</td>
                <td className="px-3 py-2">{(u.roles || []).map((r) => <span key={r} className="inline-flex items-center gap-1 mr-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#1A1D24] border border-[#2A2F3A]">{r === "ADMIN" && <ShieldCheck className="h-3 w-3 text-[#2563EB]" />}{r}</span>)}</td>
                <td className="px-3 py-2"><StatusBadge user={u} /></td>
                <td className="px-3 py-2 whitespace-nowrap"><BrokerCell b={u.broker_account} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-[10px] font-bold text-slate-300" data-testid={`user-products-${u.email}`}>
                  {{ FUTURES_ONLY: "Futures", OPTIONS_ONLY: "Options", BOTH: "Futures + Options" }[u.broker_account?.trading_products] || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-400">{dateTime(u.last_login_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-400">{dateTime(u.created_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-slate-400">{u.created_by?.name || u.created_by?.email || (u.created_via === "MIGRATION" ? "System migration" : "—")}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">No users match.</td></tr>}
          </tbody>
        </table>
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} roles={data.assignable_roles} onCreated={load} />
      {openId && <UserDrawer userId={openId} roles={data.assignable_roles} me={me} users={data.users} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
